import type { PayloadAction } from '@reduxjs/toolkit';

import { createSlice } from '@reduxjs/toolkit';

import type { CartItem } from '../types';

/**
 * Billing State Interface
 */
interface BillingState {
  /** Currently loaded tenant ID */
  currentTenantId?: string | null;
  /** Items currently in the cart */
  cartItems: CartItem[];
  /** Current active subscription data */
  activeSubscription: any | null;
  /** Available plans from API */
  availablePlans: any[];
  /** Available addons from API */
  availableAddons: any[];
  /** Current active tab in billing dashboard */
  activeTab: string;
  /** Current active invoice for payment */
  selectedInvoice: any | null;
  /** Current estimate results */
  estimateResult: any | null;
  /** Available plans list */
  plans: any[];
  /** Transformed addons data for UI sections */
  addonsData: any[];
  /** Fetching status */
  loading: boolean;
  /** UI State: Dialog visibility */
  dialogs: {
    payment: boolean;
    confirmation: boolean;
    invoice: boolean;
  };
  /** Pre-selected plan period when navigating to change plan ('month' | 'year') */
  targetPlanPeriod?: 'month' | 'year' | null;
}

const initialState: BillingState = {
  currentTenantId: null,
  cartItems: [],
  activeSubscription: null,
  availablePlans: [],
  availableAddons: [],
  activeTab: 'currentPlan',
  selectedInvoice: null,
  estimateResult: null,
  plans: [],
  addonsData: [],
  loading: false,
  dialogs: {
    payment: false,
    confirmation: false,
    invoice: false,
  },
  targetPlanPeriod: null,
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    /**
     * Add or update an item in the cart
     */
    addToCart: (state, action: PayloadAction<CartItem>) => {
      const newItem = action.payload;

      // If it's a plan, replace existing plan instead of adding
      if (newItem.item_type === 'plan') {
        state.cartItems = state.cartItems.filter((item) => item.item_type !== 'plan');
      }

      const existingIndex = state.cartItems.findIndex(
        (item) =>
          (item.id === newItem.id && item.item_type === newItem.item_type) ||
          (item.id?.toLowerCase() === newItem.id?.toLowerCase() &&
            item.item_type === newItem.item_type)
      );

      if (existingIndex >= 0) {
        state.cartItems[existingIndex] = {
          ...state.cartItems[existingIndex],
          ...newItem,
        };
      } else {
        state.cartItems.push(newItem);
      }
    },

    /**
     * Remove an item from the cart
     */
    removeFromCart: (state, action: PayloadAction<{ id: string; item_type: string }>) => {
      state.cartItems = state.cartItems.filter(
        (item) => !(item.id === action.payload.id && item.item_type === action.payload.item_type)
      );
    },

    /**
     * Update item quantity
     */
    updateQuantity: (
      state,
      action: PayloadAction<{ id: string; item_type: string; quantity: number }>
    ) => {
      const item = state.cartItems.find(
        (i) => i.id === action.payload.id && i.item_type === action.payload.item_type
      );
      if (item) {
        item.quantity = action.payload.quantity;
      }
    },

    /**
     * Update item coupon
     */
    updateCoupon: (
      state,
      action: PayloadAction<{ id: string; item_type: string; couponId: string | undefined }>
    ) => {
      const item = state.cartItems.find(
        (i) => i.id === action.payload.id && i.item_type === action.payload.item_type
      );
      if (item) {
        item.couponId = action.payload.couponId;
      }
    },

    /**
     * Clear the entire cart
     */
    clearCart: (state) => {
      state.cartItems = [];
      state.estimateResult = null;
    },

    /**
     * Set the entire cart items (useful for bulk updates)
     */
    setCartItems: (state, action: PayloadAction<CartItem[]>) => {
      state.cartItems = action.payload;
    },

    /**
     * Set active subscription
     */
    setActiveSubscription: (state, action: PayloadAction<any>) => {
      state.activeSubscription = action.payload;
    },

    /**
     * Set available data
     */
    setBillingData: (
      state,
      action: PayloadAction<{ plans: any[]; addonsData: any[]; currentTenantId?: string }>
    ) => {
      state.plans = action.payload.plans;
      state.addonsData = action.payload.addonsData;
      if (action.payload.currentTenantId !== undefined) {
        state.currentTenantId = action.payload.currentTenantId;
      }
    },

    /**
     * Set loading status
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    /**
     * Set estimate result
     */
    setEstimateResult: (state, action: PayloadAction<any>) => {
      state.estimateResult = action.payload;
    },

    /**
     * Toggle dialog visibility
     */
    setDialogVisibility: (
      state,
      action: PayloadAction<{ key: keyof BillingState['dialogs']; value: boolean }>
    ) => {
      state.dialogs[action.payload.key] = action.payload.value;
    },

    /**
     * Set selected invoice
     */
    setSelectedInvoice: (state, action: PayloadAction<any | null>) => {
      state.selectedInvoice = action.payload;
    },

    /**
     * Set active tab
     */
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },

    /**
     * Set target plan period for change plan view ('month' | 'year' | null)
     */
    setTargetPlanPeriod: (state, action: PayloadAction<'month' | 'year' | null>) => {
      state.targetPlanPeriod = action.payload;
    },

    /**
     * Reset complete billing state
     */
    resetBillingState: () => initialState,
  },
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  updateCoupon,
  clearCart,
  setCartItems,
  setActiveSubscription,
  setBillingData,
  setLoading,
  setEstimateResult,
  setDialogVisibility,
  setSelectedInvoice,
  setActiveTab,
  setTargetPlanPeriod,
  resetBillingState,
} = billingSlice.actions;

export default billingSlice.reducer;
